import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DocumentCard } from "@/shared/ui";
import { Button, H2 } from "@/shared/ui";
import { getCountryName } from "@/shared/core";
import { ROUTES } from "@/shared/config";
import { formatDate } from "@/shared/core";
import { useDocuments } from "../hooks/useDocuments";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../hooks/useConfirm";
import { FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { generatePDF, downloadPDF } from "@/shared/core";

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { documents, loading, deleteDocument } = useDocuments();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { confirm, Modal } = useConfirm();

  const handleDelete = async (id: string) => {
    await confirm({
      title: t("dashboard.deleteDocument.title"),
      message: t("dashboard.deleteDocument.message"),
      confirmText: t("dashboard.deleteDocument.confirm"),
      cancelText: t("dashboard.deleteDocument.cancel"),
      variant: "danger",
      onConfirm: async () => {
        setDeletingId(id);
        try {
          await deleteDocument(id);
          toast.success(t("dashboard.documentDeleted"));
        } catch {
          toast.error(t("dashboard.deleteError"));
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handleDownload = (doc: {
    title: string;
    content: string;
    country: string;
    created_at: string;
  }) => {
    try {
      const pdf = generatePDF({
        title: doc.title,
        content: doc.content,
        country: getCountryName(doc.country),
        date: formatDate(doc.created_at),
        showBranding: profile?.plan === "free",
      });
      downloadPDF(pdf, doc.title);
      toast.success(t("dashboard.pdfDownloaded"));
    } catch {
      toast.error(t("dashboard.downloadError"));
    }
  };

  const handleView = (id: string) => {
    navigate(`${ROUTES.DOCUMENT}/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-nieve flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500 mx-auto mb-4"></div>
          <p className="text-titan">{t("dashboard.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nieve py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <H2 className="mb-2">{t("dashboard.title")}</H2>
              <p className="text-titan">
                {profile?.user_type === "lawyer"
                  ? t("lawyer.dashboard.subtitle")
                  : profile?.plan === "pro"
                    ? t("dashboard.subtitle")
                    : t("dashboard.subtitleFree", {
                        credits: profile?.credits_remaining || 0,
                      })}
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => navigate(ROUTES.GENERATE)}
              className="flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t("dashboard.newDocument")}
            </Button>
          </div>
        </div>

        {/* Empty State */}
        {documents.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-tiber-400 bg-opacity-10 rounded-full mb-6">
              <FileText className="w-10 h-10 text-tiber-500" />
            </div>
            <H2 className="mb-4">{t("dashboard.empty.title")}</H2>
            <p className="text-titan mb-8 max-w-md mx-auto">
              {t("dashboard.empty.description")}
            </p>
            <Button size="lg" onClick={() => navigate(ROUTES.GENERATE)}>
              <Plus className="w-5 h-5 mr-2" />
              {t("dashboard.empty.cta")}
            </Button>
          </div>
        ) : (
          /* Documents Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.map((doc) => (
              <DocumentCard
                key={doc.id}
                id={doc.id}
                title={doc.title}
                documentType={doc.document_type}
                country={getCountryName(doc.country)}
                countryCode={doc.country}
                createdAt={formatDate(doc.created_at)}
                status={doc.status as "draft" | "final"}
                onView={() => handleView(doc.id)}
                onDownload={() => handleDownload(doc)}
                onDelete={
                  deletingId === doc.id ? undefined : () => handleDelete(doc.id)
                }
              />
            ))}
          </div>
        )}

        {/* Upgrade CTA */}
        {profile?.plan === "free" && profile?.credits_remaining === 0 && (
          <div className="mt-12 bg-gradient-hero text-white rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">
              {t("dashboard.upgrade.title")}
            </h3>
            <p className="text-lg mb-6">{t("dashboard.upgrade.description")}</p>
            <Button
              size="lg"
              onClick={() => navigate(ROUTES.PRICING)}
              className="bg-white text-tiber-500 hover:bg-romance-50"
            >
              {t("dashboard.upgrade.button")}
            </Button>
          </div>
        )}
      </div>
      {Modal}
    </div>
  );
};
